import type { SendEventParams } from '~/shared/analytix'
import type { NavigationParameters } from '../../routing/index'
import type { PlayerMeta } from '../player-meta'
import type { PlayerOpenParameters } from './player-open'

import { api } from '@setplex/tria-api'
import { createWatch, restore, sample } from 'effector'
import { model as mqModel } from '~/shared/mq'
import { KEY_MAP } from '~/shared/routes/query'
import { SnapshotMap } from '../../general/snapshot-map'
import { parameters as navigationParameters } from '../../routing/index'
import { playStart, playStop } from '../models/play-start-stop'
import { PlayerMetaMessage } from '../player-meta'
import { parameters as playerOpenParameters } from './player-open'

const SNAPSHOTS_TO_KEEP = 10
let latestHttpErrorCode = ''

export type PlayStopParameters = SendEventParams.PlayStop & {
  '(playerId)'?: string[] // auxiliary parameter to store players id for internal use, it will not be sent to analytix
}

/**
 * SnapshotMap to store `play_stop` event parameters with their history
 */
export const parameters = new SnapshotMap<PlayStopParameters>(SNAPSHOTS_TO_KEEP)

/**
 * Baseline state for `totalWatchedTimeFromLatestPlayStart`:
 * - reset on every new play_start
 * - measured exactly once on first following play_stop
 * - any additional play_stop before next play_start reports 0
 */
let latestPlayStartSecond: number | null = null
let durationMeasuredForLatestStart = false

//
// Update parameters
//

function fromPlayerOpenParameters(params: SnapshotMap<PlayerOpenParameters>) {
  parameters
    .update('(playerId)', params.get('(playerId)'))
    .update('contentId', params.get('contentId'))
    .update('contentType', params.get('contentType'))
    .update('contentUrl', params.get('contentUrl'))
    .update('contentName', params.get('contentName'))
    .update('programName', params.get('programName'))
    .update('isContentPaid', params.get('isContentPaid'))
    .commit()
    .notify()
}

function fromNavigationParameters(params: SnapshotMap<NavigationParameters>) {
  const categoryName = getCategoryNameFromSearch(params.get('search'))
  if (!categoryName) return

  parameters.update('categoryName', categoryName).commit().notify()
}

function fromPlayerState(snapshot: {
  playerId: string
  currentTime: string
  contentLength: string
  httpErrorCode: string
  contentErrorCode: string
  audioTrackLanguage: string
  subtitlesLanguage: string
  videoQuality: string
  playbackSessionId: string
}) {
  const stopSecond = toSecond(snapshot.currentTime)
  const totalWatchedTimeFromLatestPlayStart =
    durationMeasuredForLatestStart || latestPlayStartSecond == null
      ? '0'
      : String(Math.max(0, stopSecond - latestPlayStartSecond))

  const httpErrorCode = snapshot.httpErrorCode || latestHttpErrorCode

  parameters
    .update('(playerId)', [snapshot.playerId])
    .update('stopSecond', snapshot.currentTime)
    .update('contentLength', snapshot.contentLength)
    .update('httpErrorCode', httpErrorCode)
    .update('contentErrorCode', snapshot.contentErrorCode)
    .update('audioTrackLanguage', snapshot.audioTrackLanguage)
    .update('subtitlesLanguage', snapshot.subtitlesLanguage)
    .update('videoQuality', snapshot.videoQuality)
    .update('playbackSessionId', snapshot.playbackSessionId)
    .update(
      'totalWatchedTimeFromLatestPlayStart',
      totalWatchedTimeFromLatestPlayStart
    )
    .commit()
    .notify()

  durationMeasuredForLatestStart = true
}

function onPlayStart(snapshot: { currentTime: string }) {
  latestPlayStartSecond = toSecond(snapshot.currentTime)
  durationMeasuredForLatestStart = false
}

function toSecond(raw?: string): number {
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed < 0) return 0
  return Math.round(parsed)
}

function getCategoryNameFromSearch(search?: string): string | undefined {
  if (!search) return undefined

  const normalized = search.startsWith('?') ? search.slice(1) : search
  const query = new URLSearchParams(normalized)

  return (
    query.get('categoryName') || query.get(KEY_MAP.categoryName) || undefined
  )
}

function fromMeta(params: PlayerMeta) {
  parameters
    .update('categoryName', params.trailerCategoryName)
    .commit()
    .notify()
}

//
// Updates listeners
//

// on player open parameters update
playerOpenParameters.updates.on(() => {
  fromPlayerOpenParameters(playerOpenParameters)
})

navigationParameters.updates.on(() => {
  fromNavigationParameters(navigationParameters)
})

createWatch({
  unit: sample({
    source: restore(mqModel.on(PlayerMetaMessage), null),
    // auto trailer does not have special route name, so we use category name from params.
    filter: (message) =>
      message != null && Boolean(message.meta.trailerCategoryName),
    fn: (message) => message!.meta,
  }),
  fn: (meta) => fromMeta(meta),
})

createWatch({
  unit: playStart,
  fn: ({ snapshot }) => {
    onPlayStart(snapshot)
  },
})

createWatch({
  unit: playStop,
  fn: ({ snapshot }) => {
    fromPlayerState(snapshot)
  },
})

createWatch({
  unit: api.player.$currentPlayerPageUrlError,
  fn: (error) => {
    latestHttpErrorCode = String(error?.errorCode ?? error?.code ?? '')
  },
})
